import React, { useCallback, useEffect, useState } from 'react';
import { ArrowDownTrayIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import AnimatedModal from '../ui/AnimatedModal';

export function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Shows a server-generated PDF exactly as it downloads: the same file is previewed and saved,
 * so what HR sees is what prints. `load()` resolves to { blob, fileName }.
 */
export default function PdfPreviewModal({ isOpen, onClose, title, subtitle, icon, load }) {
  const [state, setState] = useState({ loading: true, error: false, pdf: null, url: null });

  const fetchPdf = useCallback(() => {
    let cancelled = false;
    setState({ loading: true, error: false, pdf: null, url: null });
    load()
      .then((pdf) => !cancelled && setState({ loading: false, error: false, pdf, url: URL.createObjectURL(pdf.blob) }))
      .catch(() => !cancelled && setState({ loading: false, error: true, pdf: null, url: null }));
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => (isOpen ? fetchPdf() : undefined), [isOpen, fetchPdf]);
  useEffect(() => () => state.url && URL.revokeObjectURL(state.url), [state.url]);

  const { loading, error, pdf, url } = state;

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      zIndex={10000}
      contentClassName="max-w-5xl w-full bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden mx-auto my-6"
    >
      <div className="flex h-[88vh] flex-col">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            {icon && (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-indigo-100 bg-indigo-50 text-indigo-600">
                {icon}
              </div>
            )}
            <div className="min-w-0">
              <h2 className="truncate text-lg font-bold text-slate-900">{title}</h2>
              {subtitle && <p className="truncate text-sm text-slate-500">{subtitle}</p>}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {url && (
              // Some phones cannot show a PDF inside a page; a new tab always can.
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-xl p-2 text-slate-500 hover:bg-slate-100"
                title="Open in a new tab"
                aria-label="Open in a new tab"
              >
                <ArrowTopRightOnSquareIcon className="h-5 w-5" />
              </a>
            )}
            <button
              type="button"
              onClick={() => saveBlob(pdf.blob, pdf.fileName)}
              disabled={!pdf}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#007BFF] px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0056B3] disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Download PDF</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center bg-slate-100">
          {loading ? (
            <div className="flex flex-col items-center gap-3 text-sm text-slate-500">
              <span className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-[#007BFF]" />
              Preparing the PDF…
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 text-sm text-slate-600">
              Could not create the PDF.
              <button
                type="button"
                onClick={fetchPdf}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 font-semibold text-slate-700 hover:bg-slate-50"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Try again
              </button>
            </div>
          ) : (
            <iframe title={title} src={url} className="h-full w-full" />
          )}
        </div>
      </div>
    </AnimatedModal>
  );
}
